// PasskeyService.js - WebAuthn/FIDO2パスキー認証のフロントエンドサービス

import ApiFetch from "../components/ApiFetch";

class PasskeyService {
  constructor() {
    // ApiFetchと同じ形式でAPIエンドポイントを構築
    this.apiBaseUrl = `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/passkey`;
  }

  // WebAuthnが利用可能かチェック
  isAvailable() {
    return (
      window.PublicKeyCredential !== undefined &&
      navigator.credentials !== undefined &&
      typeof navigator.credentials.create === 'function' &&
      typeof navigator.credentials.get === 'function'
    );
  }

  // プラットフォーム認証器(デバイス内蔵)が利用可能かチェック
  async isPlatformAuthenticatorAvailable() {
    if (!this.isAvailable()) return false;
    try {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (e) {
      console.error('Platform authenticator check failed:', e);
      return false;
    }
  }

  // Base64URL → ArrayBuffer
  base64UrlToArrayBuffer(base64url) {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // ArrayBuffer → Base64URL
  arrayBufferToBase64Url(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  // パスキー登録開始
  async beginRegistration() {
    try {
      const response = await fetch(this.apiBaseUrl + '/registration/begin', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (!data.result) {
        throw new Error(data.error || '登録開始に失敗しました');
      }

      return data;
    } catch (error) {
      console.error('Registration begin error:', error);
      throw error;
    }
  }

  // パスキー登録完了
  async completeRegistration(options, challengeId, deviceName) {
    try {
      // バックエンドから返されたoptionsをWebAuthn APIの形式に変換
      const publicKey = {
        ...options,
        challenge: this.base64UrlToArrayBuffer(options.challenge),
        user: {
          ...options.user,
          id: this.base64UrlToArrayBuffer(options.user.id),
        },
      };

      // excludeCredentialsがあれば変換
      if (options.excludeCredentials && options.excludeCredentials.length > 0) {
        publicKey.excludeCredentials = options.excludeCredentials.map(
          (cred) => ({
            ...cred,
            id: this.base64UrlToArrayBuffer(cred.id),
          })
        );
      }

      // WebAuthn APIで認証器に登録
      const credential = await navigator.credentials.create({ publicKey });

      if (!credential) {
        throw new Error('認証器での登録がキャンセルされました');
      }

      // サーバーに送信する形式に変換
      const credentialData = {
        id: credential.id,
        rawId: this.arrayBufferToBase64Url(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: this.arrayBufferToBase64Url(
            credential.response.attestationObject
          ),
          clientDataJSON: this.arrayBufferToBase64Url(
            credential.response.clientDataJSON
          ),
        },
      };

      // サーバーで検証
      const response = await fetch(this.apiBaseUrl + '/registration/complete', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: credentialData,
          challengeId,
          deviceName,
        }),
      });

      const data = await response.json();
      if (!data.result) {
        throw new Error(data.error || '登録に失敗しました');
      }

      // localStorageにcredential_idを保存（このブラウザで登録したことを記録）
      if (data.credentialId) {
        localStorage.setItem('passkey_credential_id', credentialData.id);
      }

      return data;
    } catch (error) {
      console.error('Registration complete error:', error);
      throw error;
    }
  }

  // パスキー認証開始
  async beginAuthentication(userId = null) {
    try {
      const response = await fetch(this.apiBaseUrl + '/authentication/begin', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      const data = await response.json();
      if (!data.result) {
        throw new Error(data.error || '認証開始に失敗しました');
      }

      return data;
    } catch (error) {
      console.error('Authentication begin error:', error);
      throw error;
    }
  }

  // パスキー認証完了
  async completeAuthentication(options, challengeId) {
    try {
      // バックエンドから返されたoptionsをWebAuthn APIの形式に変換
      const publicKey = {
        ...options,
        challenge: this.base64UrlToArrayBuffer(options.challenge),
      };

      // allowCredentialsがあれば変換
      if (options.allowCredentials && options.allowCredentials.length > 0) {
        publicKey.allowCredentials = options.allowCredentials.map(
          (cred) => ({
            ...cred,
            id: this.base64UrlToArrayBuffer(cred.id),
          })
        );
      }

      // WebAuthn APIで認証
      const credential = await navigator.credentials.get({ publicKey });

      if (!credential) {
        throw new Error('認証がキャンセルされました');
      }

      // サーバーに送信する形式に変換
      const credentialData = {
        id: credential.id,
        rawId: this.arrayBufferToBase64Url(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: this.arrayBufferToBase64Url(
            credential.response.authenticatorData
          ),
          clientDataJSON: this.arrayBufferToBase64Url(
            credential.response.clientDataJSON
          ),
          signature: this.arrayBufferToBase64Url(credential.response.signature),
          userHandle: credential.response.userHandle
            ? this.arrayBufferToBase64Url(credential.response.userHandle)
            : null,
        },
      };

      // サーバーで検証
      const response = await fetch(
        this.apiBaseUrl + '/authentication/complete',
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            credential: credentialData,
            challengeId,
          }),
        }
      );

      const data = await response.json();
      if (!data.result) {
        throw new Error(data.error || '認証に失敗しました');
      }

      return data;
    } catch (error) {
      console.error('Authentication complete error:', error);
      throw error;
    }
  }

  // パスキー一覧取得
  async listPasskeys() {
    try {
      const response = await fetch(this.apiBaseUrl + '/list', {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      if (!data.result) {
        throw new Error(data.error || 'パスキー一覧の取得に失敗しました');
      }

      return data.passkeys || [];
    } catch (error) {
      console.error('List passkeys error:', error);
      throw error;
    }
  }

  // パスキー削除
  async deletePasskey(credentialId) {
    try {
      const response = await fetch(this.apiBaseUrl + `/${credentialId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await response.json();
      if (!data.result) {
        throw new Error(data.error || 'パスキーの削除に失敗しました');
      }

      // localStorageからcredential_idを削除
      const storedCredentialId = localStorage.getItem('passkey_credential_id');
      if (storedCredentialId === credentialId) {
        localStorage.removeItem('passkey_credential_id');
      }

      return data;
    } catch (error) {
      console.error('Delete passkey error:', error);
      throw error;
    }
  }

  // パスキー登録フローの実行
  async register(deviceName = null) {
    // 利用可能性チェック
    if (!this.isAvailable()) {
      throw new Error(
        'このブラウザはパスキーに対応していません。Chrome、Safari、Edgeなどの最新ブラウザをご利用ください。'
      );
    }

    // デバイス名の設定
    const finalDeviceName =
      deviceName || this.getDefaultDeviceName() || 'マイデバイス';

    // 登録開始
    const beginData = await this.beginRegistration();

    // 認証器での登録
    const result = await this.completeRegistration(
      beginData.options,
      beginData.challengeId,
      finalDeviceName
    );

    return result;
  }
  
  // このブラウザで使えるパスキーが登録されているかチェック
  async hasPasskeyAvailable() {
    // WebAuthn対応チェック
    if (!this.isAvailable()) {
      return false;
    }
    
    // localStorageにcredential_idが保存されているかチェック
    // ログインしていない状態でもlocalStorageは確認できる
    const storedCredentialId = localStorage.getItem('passkey_credential_id');
    return !!storedCredentialId;
  }

  // パスキー認証フローの実行
  async authenticate(userId = null) {
    // 利用可能性チェック
    if (!this.isAvailable()) {
      throw new Error(
        'このブラウザはパスキーに対応していません。Chrome、Safari、Edgeなどの最新ブラウザをご利用ください。'
      );
    }

    // 認証開始
    const beginData = await this.beginAuthentication(userId);

    // 認証器での認証
    const result = await this.completeAuthentication(
      beginData.options,
      beginData.challengeId
    );

    return result;
  }

  // デフォルトのデバイス名を取得（より詳細に）
  getDefaultDeviceName() {
    const ua = navigator.userAgent;
    let deviceName = '';
    
    // デバイス種類の判定
    if (/iPhone/.test(ua)) {
      // iPhoneのモデル判定（おおよその判定）
      if (window.screen.height >= 926) {
        deviceName = 'iPhone 15 Pro Max / 14 Pro Max';
      } else if (window.screen.height >= 852) {
        deviceName = 'iPhone 15 Pro / 14 Pro';
      } else if (window.screen.height >= 844) {
        deviceName = 'iPhone 15 / 14 / 13 / 12';
      } else {
        deviceName = 'iPhone';
      }
    } else if (/iPad/.test(ua)) {
      deviceName = 'iPad';
      if (window.screen.width >= 1024) {
        deviceName = 'iPad Pro';
      }
    } else if (/Android/.test(ua)) {
      // Androidの機種名抽出を試みる
      const modelMatch = ua.match(/Android.*;\s*([^;)]+)\s*Build/);
      if (modelMatch && modelMatch[1]) {
        deviceName = modelMatch[1].trim();
      } else {
        deviceName = 'Android端末';
      }
    } else if (/Macintosh/.test(ua)) {
      // Macの判定
      if (/Mac OS X 10_15/.test(ua)) {
        deviceName = 'MacBook (Catalina)';
      } else if (/Mac OS X 11/.test(ua)) {
        deviceName = 'MacBook (Big Sur)';
      } else if (/Mac OS X 12/.test(ua)) {
        deviceName = 'MacBook (Monterey)';
      } else if (/Mac OS X 13/.test(ua)) {
        deviceName = 'MacBook (Ventura)';
      } else if (/Mac OS X 14/.test(ua)) {
        deviceName = 'MacBook (Sonoma)';
      } else {
        deviceName = 'Mac';
      }
    } else if (/Windows NT/.test(ua)) {
      // Windowsのバージョン判定
      if (/Windows NT 10.0/.test(ua)) {
        deviceName = 'Windows 10/11 PC';
      } else if (/Windows NT 6.3/.test(ua)) {
        deviceName = 'Windows 8.1 PC';
      } else {
        deviceName = 'Windows PC';
      }
    } else if (/Linux/.test(ua)) {
      deviceName = 'Linux PC';
    } else {
      deviceName = 'デバイス';
    }
    
    // ブラウザ情報を追加
    let browser = '';
    if (/Edg\//.test(ua)) {
      browser = ' (Edge)';
    } else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) {
      browser = ' (Chrome)';
    } else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
      browser = ' (Safari)';
    } else if (/Firefox\//.test(ua)) {
      browser = ' (Firefox)';
    }
    
    return deviceName + browser;
  }
}

export default new PasskeyService();
